import PropTypes from 'prop-types';
import { forwardRef, useMemo, useState } from 'react';
import useInvoiceUnitsMeasurement from 'Hooks/useInvoiceUnitsMeasurement';
import { cn } from 'Utils/cn';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    flip,
    useMergeRefs,
    autoUpdate,
    size,
    FloatingPortal,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import OutlineChevronDown from 'Components/Atoms/OutlineIcons/OutlineChevronDown';
import SelectFilter from 'Components/Atoms/Select/SelectFilter';
import { hasMatch } from 'Utils/strings';
import { useTranslation } from 'react-i18next';

// Milligrams (mg) -> mg
const getAbbreviation = (text) => {
    const match = text.match(/\(([^)]+)\)/);
    return match ? match[1] : text;
};

const SelectInvoiceUnitsMeasurement = forwardRef(function SelectInvoiceUnitsMeasurement(props, ref) {
    const { value, disabled, onChange, className } = props;

    const { invoiceUnitsMeasurement, isLoading } = useInvoiceUnitsMeasurement();
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const { refs, floatingStyles, context, strategy, x, y } = useFloating({
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        placement: 'bottom-start',
        strategy: 'fixed',
        middleware: [
            flip(),
            size({
                apply({ elements, availableHeight }) {
                    Object.assign(elements.floating.style, {
                        maxHeight: `${Math.min(availableHeight - 20, 520)}px`,
                    });
                },
            }),
        ],
    });

    const { getFloatingProps, getReferenceProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'listbox' }),
        useDismiss(context),
    ]);

    const mergedRef = useMergeRefs([ref, refs.setReference]);
    const { t } = useTranslation('common');

    const options = useMemo(() => {
        return invoiceUnitsMeasurement.map((unit) => ({
            value: unit.code,
            label: unit.name,
        }));
    }, [invoiceUnitsMeasurement]);

    const labelSelected = options.find((option) => option.value === value)?.label || '';
    const labelAbbreviation = getAbbreviation(labelSelected);
    const optionsFiltered = query.trim() ? options.filter((option) => hasMatch(option.label, query)) : options;
    const handleFilter = (ev) => {
        setQuery(ev.target.value);
    };

    if (isLoading) return <div className="h-9" />;

    return (
        <div className={cn('w-full', className)}>
            <button
                type="button"
                ref={mergedRef}
                {...getReferenceProps({
                    className: cn(
                        'overflow-hidden bg-transparent w-full flex items-center gap-0.5 px-1 text-left disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
                        '[&_svg]:pointer-events-none [&_svg]:shrink-0 shrink-0',
                        'rounded-sm focus-visible:outline-none focus-visible:bg-primary/10 focus-visible:text-primary h-9',
                    ),
                    disabled,
                })}
            >
                <div className="flex-grow text-xs truncate min-w-0">{labelAbbreviation}</div>
                <OutlineChevronDown size={16} color="currentColor" />
            </button>

            <AnimatePresence>
                {open && (
                    <FloatingPortal>
                        <motion.div
                            ref={refs.setFloating}
                            style={{
                                ...floatingStyles,
                                position: strategy,
                                top: y ?? 0,
                                left: x ?? 0,
                            }}
                            className="w-[180px] overflow-x-hidden overflow-y-auto scrollbar-thin rounded-md bg-white shadow-sm ring-1 ring-black/5 focus:outline-none text-sm z-dropdown"
                            tabIndex={-1}
                            {...getFloatingProps()}
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                        >
                            <SelectFilter value={query} onChange={handleFilter} />

                            {optionsFiltered.length === 0 && !!query && (
                                <div className="text-center text-gray-500 text-sm py-6 px-1.5">
                                    {t('no-results-found')}
                                </div>
                            )}

                            <ul className="p-1.5">
                                {optionsFiltered.map((opt) => {
                                    return (
                                        <li
                                            key={opt.value}
                                            className={cn(
                                                'cursor-pointer select-none px-3 py-2 rounded-sm transition-colors',
                                                value === opt.value
                                                    ? 'bg-primary/10 text-primary font-semibold'
                                                    : 'hover:bg-gray-100',
                                                opt.className,
                                            )}
                                            role="option"
                                            aria-selected={value === opt.value}
                                            onClick={() => {
                                                onChange(opt.value);
                                                setOpen(false);
                                            }}
                                        >
                                            {opt.label}
                                        </li>
                                    );
                                })}
                            </ul>
                        </motion.div>
                    </FloatingPortal>
                )}
            </AnimatePresence>
        </div>
    );
});

export default SelectInvoiceUnitsMeasurement;

SelectInvoiceUnitsMeasurement.propTypes = {
    disabled: PropTypes.bool,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    className: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    highlight: PropTypes.bool,
};
